import { type AdminStats, TASK_STATES } from '@agentic/contracts';
import {
  Badge,
  formatNumber,
  formatPercent,
  formatUsd,
  KeyValue,
  lifecycleKind,
  Section,
  SkeletonRows,
  StatTile,
  statusTone,
  Table,
} from '@agentic/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { config } from '../../app/config.ts';
import { healthQuery, statsQuery } from '../../app/queries.ts';
import { ErrorBanner, QueryError } from '../../app/route-states.tsx';
import { PAGE, PageHeader } from '../../app/shell.tsx';
import { TestingTools } from '../../features/admin/testing-tools.tsx';
import { costText } from '../../features/model-management/format.ts';

export const Route = createFileRoute('/_app/admin')({ component: AdminPage });

function AdminPage() {
  return (
    <>
      <PageHeader
        title="Admin"
        eyebrow="Monitoring"
        description="Health and governance numbers. Testing tools for the demo environment are at the bottom, collapsed."
        actions={
          <>
            <a
              className="wb-btn inline-flex h-[36px] items-center gap-1.5 rounded-full border border-line-strong bg-surface px-4 text-sm font-medium text-ink transition-shadow duration-150 hover:shadow-float"
              href={config.grafanaUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              Grafana <ExternalLink className="size-3.5" aria-hidden />
            </a>
            <a
              className="wb-btn inline-flex h-[36px] items-center gap-1.5 rounded-full border border-line-strong bg-surface px-4 text-sm font-medium text-ink transition-shadow duration-150 hover:shadow-float"
              href={config.jaegerUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              Jaeger <ExternalLink className="size-3.5" aria-hidden />
            </a>
          </>
        }
      />
      <div className={PAGE}>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <Stats />
          <Health />
        </div>
        <TestingTools />
      </div>
    </>
  );
}

function Stats() {
  const q = useQuery(statsQuery);
  if (q.isPending) return <SkeletonRows rows={3} label="Loading stats" />;
  if (q.isError) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  const s = q.data;
  return (
    <Section
      title="Governance"
      actions={<span className="font-mono text-[12px] text-ink-3">live · 5 s</span>}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="stats">
        <StatTile label="Success rate (24 h)" value={formatPercent(s.successRate24h)} tone="ok" />
        <StatTile
          label="Invalid action rate"
          value={formatPercent(s.invalidActionRate)}
          hint="denied / all receipts"
        />
        <StatTile
          label="Unauthorized attempts"
          value={formatNumber(s.unauthorizedAttempts)}
          tone="accent"
          hint="gateway denials"
        />
        <StatTile
          label="Unknown outcomes"
          value={formatNumber(s.unknownOutcomes)}
          tone={s.unknownOutcomes ? 'warn' : 'neutral'}
        />
        <StatTile label="Approval backlog" value={formatNumber(s.approvalBacklog)} tone="accent" />
        <StatTile
          label="Cost per task"
          value={s.meanCostPerTaskUsd === null ? 'unknown' : formatUsd(s.meanCostPerTaskUsd)}
          hint={costHint(s)}
        />
        <StatTile
          label="Queue age"
          value={s.queueAgeSeconds === null ? '—' : `${Math.round(s.queueAgeSeconds)} s`}
        />
        <StatTile
          label="Tasks"
          value={formatNumber(Object.values(s.tasksByState).reduce((a, b) => a + b, 0))}
        />
      </div>
      <CostCoverage stats={s} />
      <div className="mt-5 border-t border-line pt-4">
        <div className="mb-2 text-[13px] text-ink-3">Tasks by state</div>
        <dl className="flex flex-wrap gap-x-5 gap-y-1.5">
          {TASK_STATES.filter((st) => s.tasksByState[st]).map((st) => (
            <div key={st} className="flex items-baseline gap-1.5">
              <dt className="font-mono text-[12px] text-ink-2">{st}</dt>
              <dd className="font-mono text-[13px] font-medium">{s.tasksByState[st]}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Section>
  );
}

/** 23: what the mean cost covers (`meanCostPerTaskStatus`); unknown is never shown as $0. */
function costHint(s: AdminStats): string {
  if (s.meanCostPerTaskUsd === null) return 'no model call has a known price';
  switch (s.meanCostPerTaskStatus) {
    case 'partial':
      return 'mean of the priced part; some calls have no known price';
    case 'unknown':
      return 'no model call has a known price';
    default:
      return 'mean, model tokens';
  }
}

/** Call-record cost coverage and cost by actual model (23), when the server reports them. */
function CostCoverage({ stats: s }: { stats: AdminStats }) {
  if (!s.costCoverage && !s.costByModel?.length) return null;
  return (
    <div className="mt-5 border-t border-line pt-4" data-testid="cost-coverage">
      {s.costCoverage && (
        <p className="text-[13px] text-ink-2">
          Cost coverage of model calls: {formatNumber(s.costCoverage.known)} known ·{' '}
          {formatNumber(s.costCoverage.partial)} partial · {formatNumber(s.costCoverage.unknown)} unknown.
          Estimates from recorded prices, not provider invoices.
        </p>
      )}
      {s.costByModel && s.costByModel.length > 0 && (
        <div className="-mx-2 mt-3">
          <Table aria-label="Cost by model" scrollLabel="Cost by model">
            <thead>
              <tr>
                <th scope="col">Configured model</th>
                <th scope="col">Reported model</th>
                <th scope="col">Calls</th>
                <th scope="col">Tokens in / out</th>
                <th scope="col">Known cost</th>
              </tr>
            </thead>
            <tbody>
              {s.costByModel.map((m) => (
                <tr key={`${m.requestedModel}|${m.reportedModel ?? ''}|${m.connectionId ?? ''}`}>
                  <td className="font-mono text-[12.5px]">
                    {m.requestedModel}
                    {m.profileName && <div className="text-[12px] text-ink-3">{m.profileName}</div>}
                  </td>
                  <td className="font-mono text-[12.5px]">{m.reportedModel ?? 'not reported'}</td>
                  <td className="font-mono text-[12.5px]">{formatNumber(m.calls)}</td>
                  <td className="font-mono text-[12.5px]">
                    {formatNumber(m.inputTokens)} / {formatNumber(m.outputTokens)}
                  </td>
                  <td className="font-mono text-[12.5px]">
                    {costText(m.costStatus === 'unknown' ? null : m.knownCostUsd, m.costStatus)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

function Health() {
  const q = useQuery(healthQuery);
  return (
    <Section title="Health">
      {q.isPending ? (
        <SkeletonRows rows={2} label="Loading health" />
      ) : q.isError ? (
        <ErrorBanner error={q.error} title="Platform not answering" onRetry={() => void q.refetch()} />
      ) : (
        <div className="flex flex-col gap-3">
          <KeyValue
            items={[
              {
                label: 'Status',
                value: (
                  <Badge tone={q.data.ok ? 'ok' : 'bad'} dot mono>
                    {q.data.ok ? 'ok' : 'degraded'}
                  </Badge>
                ),
              },
              {
                label: 'Roles',
                value: <code className="font-mono text-[12.5px]">{q.data.roles.join(', ')}</code>,
              },
              {
                label: 'Profile',
                value: <code className="font-mono text-[12.5px]">{q.data.profile ?? '—'}</code>,
              },
              {
                label: 'Version',
                value: <code className="font-mono text-[12.5px]">{q.data.version ?? '—'}</code>,
              },
              {
                label: 'Uptime',
                value:
                  q.data.uptimeSeconds === undefined ? '—' : `${Math.round(q.data.uptimeSeconds / 60)} min`,
              },
            ]}
          />
          <div className="flex flex-wrap gap-1">
            {q.data.modules.map((m) => (
              <Badge
                key={m.name}
                mono
                tone={statusTone(lifecycleKind(m.state))}
                title={`${m.name}@${m.version}: ${m.state}`}
              >
                {m.name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}
